import styled from 'styled-components';
import { useParams, useLocation } from 'react-router-dom';
import { media } from '../../styles/breakpoints';
import OrgPublicationBar from 'components/common/OrgPublicationBar';
import useOrgNav from '../common/useOrgNav';
import { switchAccount, switchPublication } from '../../utils/orgSwitch';
import PublicationSidebar, { SidebarItem } from './PublicationSidebar';

/**
 * Map a publication-settings subpage to the org-nav "surface" the BE understands.
 * Only the four surfaces classic exposed an "All publications" workflow on are
 * mapped (eng-maintenance#21483); every other settings page (incl. the other
 * user-management sub-pages — activity_report, reassign_users, staffer_roles —
 * which classic did NOT give an "All publications" link) resolves to no surface
 * → default staffed-only list, no "All publications" entry.
 */
export function surfaceForPath(pathname: string): string | undefined {
  // Strip the /publications/:id prefix, then match the specific settings subpath.
  const rest = pathname.replace(/^\/publications\/\d+\//, '');
  if (rest.startsWith('integrations')) return 'integrations';
  if (rest.startsWith('portals')) return 'content_portals';
  if (rest.startsWith('tag_groups')) return 'tag_groups';
  // Only the Teams page carried the org-level "All publications" link in classic
  // — NOT activity_report / reassign_users / staffer_roles.
  if (rest === 'users/teams' || rest.startsWith('users/teams')) return 'users';
  return undefined;
}

interface PublicationLayoutProps {
  children: React.ReactNode;
}

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;

  ${media.sidenav} {
    flex-direction: column;
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 20px 30px;
  min-width: 0;

  ${media.sidenav} {
    padding: 16px;
  }
`;

export const SIDEBAR_ITEMS: SidebarItem[] = [
  { label: 'Content portals', href: '/publications/:id/portals', permissionKey: 'content_portals' },
  {
    label: 'Content request forms',
    href: '/publications/:id/content_request_forms',
    permissionKey: 'content_request_forms',
  },
  {
    label: 'Content strategy',
    href: '/publications/:id/content_strategy/report',
    permissionKey: 'content_strategy',
    expandBehavior: 'when-active',
    children: [
      { label: 'Goals', href: '/publications/:id/content_strategy/mission_and_goals' },
      { label: 'Audiences', href: '/publications/:id/content_strategy/audiences' },
      {
        label: 'Voice and style',
        href: '/publications/:id/content_strategy/voice_and_styles',
      },
      { label: 'Pillars', href: '/publications/:id/content_strategy/pillars' },
      { label: 'Content plan', href: '/publications/:id/content_strategy/plans' },
      {
        label: 'Distribution',
        href: '/publications/:id/content_strategy/distribution',
      },
      {
        label: 'SEO keywords',
        href: '/publications/:id/content_strategy/seo_keywords',
      },
    ],
  },
  { label: 'Integrations', href: '/publications/:id/integrations', permissionKey: 'integrations' },
  {
    label: 'Financial activity',
    href: '/publications/:id/invoices',
    permissionKey: 'financial_activity',
    expandBehavior: 'when-active',
    children: [
      {
        label: 'Financial transactions',
        href: '/publications/:id/transactions',
      },
    ],
  },
  { label: 'Pitch guidelines', href: '/publications/:id/pitch_guidelines', permissionKey: 'pitch_guidelines' },
  { label: 'Settings', href: '/publications/:id/settings', permissionKey: 'settings' },
  { label: 'Tags', href: '/publications/:id/tag_groups', permissionKey: 'tags' },
  {
    label: 'Templates',
    expandBehavior: 'always',
    children: [
      { label: 'Briefs', href: '/publications/:id/brief_templates', permissionKey: 'brief_templates' },
      { label: 'Custom fields', href: '/publications/:id/field_templates', permissionKey: 'custom_fields' },
      { label: 'Workflow', href: '/publications/:id/approval_flows', permissionKey: 'workflow_templates' },
    ],
  },
  {
    label: 'User management',
    href: '/publications/:id/users/teams',
    permissionKey: 'user_management',
    expandBehavior: 'when-active',
    children: [
      { label: 'User roles', href: '/publications/:id/staffer_roles', permissionKey: 'user_roles' },
      {
        label: 'Activity report',
        href: '/publications/:id/users/activity_report',
        permissionKey: 'activity_report',
      },
      {
        label: 'Reassign users',
        href: '/publications/:id/reassign_users/new',
        badge: 'NEW',
        permissionKey: 'reassign_users',
      },
    ],
    activePaths: ['/publications/:id/staff'],
  },
];

export default function PublicationLayout({
  children,
}: PublicationLayoutProps) {
  const { publicationId } = useParams<{ publicationId: string }>();
  const { pathname } = useLocation();
  const surface = surfaceForPath(pathname);
  const orgNav = useOrgNav(publicationId, surface);

  return (
    <LayoutWrapper data-testid="publication-layout">
      <OrgPublicationBar
        accounts={orgNav?.accounts ?? []}
        publications={
          orgNav?.publications?.map(p => ({ id: p.id, name: p.name })) ?? []
        }
        currentAccountName={orgNav?.account?.name}
        currentPublicationName={orgNav?.publication?.name}
        onPublicationChange={switchPublication}
        onAccountChange={switchAccount}
        allPublicationsHref={orgNav?.all_publications_path}
      />
      <ContentArea>
        <PublicationSidebar
          items={SIDEBAR_ITEMS}
          publicationId={publicationId ?? ''}
          sidebarPermissions={orgNav?.sidebar_permissions}
        />
        <MainContent>{children}</MainContent>
      </ContentArea>
    </LayoutWrapper>
  );
}
